import React from 'react';
import { Box, Typography } from '@drone-js/core';
import { RadiusType } from '@drone-js/theme';
import { CancellationPolicy } from 'gql/autogen/CancellationPolicy';
import { getCancellationPolicyDescription, getCancellationPolicyTitle, getPenaltyFromCode } from 'component/page/detail/CancellationPolicyCard/cancellationUtil';
import { useDispatch, useSelector } from 'react-redux';
import { killEvent } from 'core/utils/domUtils';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesCancellationPolicyItemClickEventFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesCancellationPolicyItemClickEventManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { CancellationPolicyInfoModal } from 'component/page/detail/CancellationPolicyCard/CancellationPolicyInfoModal';

interface CancellationPolicyItemProps {
    index: number;
    policy: CancellationPolicy;
    policyCount: number;
}

const FreeCancellationHeading: React.FC<{ text: string }> = (props) => {
    const dispatch = useDispatch();
    const openPolicyInfoModal = (e: React.KeyboardEvent | React.MouseEvent) => {
        trackElementEvent<ActivitiesCancellationPolicyItemClickEventFieldsV1>(ActivitiesCancellationPolicyItemClickEventManifestV1.ids, {});
        dispatch(showOverlay(OverlayType.RiskFreeCancellation));
        killEvent(e);
    };

    return (
        <Box data-testid="activities-cancellation-policy-item-title" as="button" px="4" textAlign="start" outlineColor="base.strong" onClick={openPolicyInfoModal}>
            <Typography variant="body-s-strong" textColor="feedback.positive">
                {props.text}
            </Typography>
        </Box>
    );
};
FreeCancellationHeading.displayName = 'FreeCancellationHeading';

const CancellationPolicyItem: React.FC<CancellationPolicyItemProps> = (props) => {
    const { index, policy, policyCount } = props;
    const cms = useSelector(selectCmsFromBootstrap);
    const penalty = getPenaltyFromCode(policy.penaltyCode);
    const policyTitle = getCancellationPolicyTitle(penalty, cms);
    const policyDescription = getCancellationPolicyDescription(policy, penalty, policyCount, cms);
    const isFreeCancellation = penalty === 0;

    // eslint-disable-next-line no-nested-ternary
    const borderRadius: RadiusType[] | undefined = index === 0 ? ['4', '4', '0', '0'] : index === policyCount - 1 ? ['0', '0', '4', '4'] : undefined;

    return (
        <Box data-element-name="activities-cancellation-policy-item" data-testid="activities-cancellation-policy-item" mb="4" pe="16" display="flex">
            <Box me="12" width="6px" backgroundColor={isFreeCancellation ? 'feedback.positive' : 'base.surface.inverted'} borderRadius={borderRadius} />
            <Box display="flex" flexDirection="column" py="4">
                {isFreeCancellation && <FreeCancellationHeading text={policyTitle} />}
                {!isFreeCancellation && (
                    <Typography data-testid="activities-cancellation-policy-item-title" variant="body-s" p="2" mt="4">
                        {policyTitle}
                    </Typography>
                )}
                <Typography variant="body-s" data-testid="activities-cancellation-policy-item-description" tag="span" p="4" mt="4">
                    {policyDescription}
                </Typography>
                {isFreeCancellation && <CancellationPolicyInfoModal hoursUntil={policy.hoursUntil || 0} />}
            </Box>
        </Box>
    );
};
CancellationPolicyItem.displayName = 'CancellationPolicyItem';

export default CancellationPolicyItem;
